import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { InterviewKind } from '@Domain/Enums/Interviews/InterviewKind';
import PlainDate from '@Domain/PlainDate';
import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import { theme } from '@Guidelines/Theme/theme';
import BackLink from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Shared/BackLink';
import useCapturePreparation from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/Creations/Hooks/useCapturePreparation';
import useLocalInterviewCreation from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/Creations/Hooks/useLocalInterviewCreation';
import useSubjectModifiability from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Hooks/useSubjectModifiability';
import SubjectBreadcrumb from '@UI/Pages/Beneficiaries/Shared/SubjectBreadcrumb';
import { css } from '@emotion/react';
import { Mic } from 'lucide-react';
import * as React from 'react';
import { Message, Skeleton } from '@verclam/ui-kit';

const BAR_COUNT = 16;
const IDLE_BARS = Array.from({ length: BAR_COUNT }, (_unused, index) => `idle-bar-${index}`);

const screenStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1.25rem;
	width: 100%;
	max-width: 45rem;
	margin: 0 auto;
`;

const headerStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
`;

const eyebrowStyle = css`
	${theme.typography.eyebrow}
	text-transform: uppercase;
	color: ${theme.colors.inkMuted};
`;

const titleStyle = css`
	${theme.typography.heroTitle}
	margin: 0;
	color: ${theme.colors.ink};
`;

const cardStyle = css`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 1.375rem;
	min-height: 25rem;
	padding: 2.5rem;
	border-radius: 2rem;
	background-color: ${theme.states.success.solid};

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		padding: 2rem 1.375rem;
		min-height: 22.5rem;
	}
`;

const cardLabelStyle = css`
	${theme.typography.eyebrow}
	color: rgba(255, 255, 255, 0.7);
`;

const timeStyle = css`
	font-family: ${theme.fonts.mono};
	font-size: clamp(3.5rem, 8vw, 4.75rem);
	font-weight: ${theme.fontWeights.medium};
	line-height: 1;
	letter-spacing: -0.03em;
	color: #fff;
`;

const barsStyle = css`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 5px;
	height: 52px;
	width: 100%;
	max-width: 420px;
`;

const barStyle = css`
	flex: none;
	width: 6px;
	height: 8px;
	border-radius: ${theme.radii.pill};
	background-color: rgba(255, 255, 255, 0.3);
`;

const hintStyle = css`
	margin: 0;
	max-width: 21.25rem;
	text-align: center;
	font-family: ${theme.fonts.body};
	font-size: 0.84375rem;
	line-height: 1.6;
	color: rgba(255, 255, 255, 0.85);
`;

const startButtonStyle = css`
	display: inline-flex;
	align-items: center;
	gap: 0.625rem;
	padding: 1.125rem 1.625rem;
	border-radius: ${theme.radii.pill};
	background-color: #fff;
	color: ${theme.states.success.solid};
	font-family: ${theme.fonts.body};
	font-size: 0.9375rem;
	font-weight: ${theme.fontWeights.semibold};
	cursor: pointer;

	&:disabled {
		cursor: not-allowed;
		opacity: 0.6;
	}
`;

const noteStyle = css`
	margin: 0;
	padding: 1rem 1.25rem;
	border-radius: ${theme.radii.large};
	background-color: ${theme.colors.surface};
	font-family: ${theme.fonts.body};
	font-size: 0.78125rem;
	line-height: 1.55;
	color: ${theme.colors.inkMuted};
`;

type Props = {
	readonly subject: SubjectAddress;
};

/**
 * The idle state of a dictated note: nothing is asked, « Commencer la dictée » writes the note to the
 * device (kind `notes`, no consent instant, name « Note dictée du {date} ») and opens its recording,
 * which starts on its own. The note belongs to the preparation read from `?preparation=`.
 */
const NoteDictationContent: React.FC<Props> = ({ subject }) => {
	const { preparationId, preparation, preparationPath } = useCapturePreparation(subject);
	const { createLocalInterview, isSubmitting, hasFailed } = useLocalInterviewCreation(subject);
	const { isKnown, canModify, isArchived, displayName } = useSubjectModifiability(subject);
	const isWriteRefused = isKnown && !canModify;

	const breadcrumbItems = React.useMemo(
		() => [{ label: preparation?.label ?? 'Préparation', url: preparationPath }, { label: 'Note' }],
		[preparation, preparationPath],
	);

	const handleStartDictation = React.useCallback(() => {
		const today = PlainDate.today();

		createLocalInterview(
			{
				name: `Note dictée du ${today.toDisplay()}`,
				kind: InterviewKind.NOTES,
				conductedOn: today.toApi(),
				participants: [],
				preparationId,
			},
			null,
		);
	}, [createLocalInterview, preparationId]);

	if (preparation === null) {
		return (
			<React.Fragment>
				<SubjectBreadcrumb subject={subject} items={breadcrumbItems} />
				<Skeleton height="20rem" animation="wave" shape="rectangle" />
			</React.Fragment>
		);
	}

	return (
		<div css={screenStyle}>
			<SubjectBreadcrumb subject={subject} items={breadcrumbItems} />
			<BackLink to={preparationPath} label={preparation.label} />

			<div css={headerStyle}>
				<span css={eyebrowStyle}>
					{displayName !== null ? `${displayName} · ${preparation.label}` : preparation.label}
				</span>
				<h1 css={titleStyle}>Note</h1>
			</div>

			<ConditionalLoader data={isArchived}>
				<Message
					severity="warn"
					text="Ce dossier est archivé : la création d'une note n'est plus possible."
				/>
			</ConditionalLoader>

			<ConditionalLoader data={hasFailed}>
				<Message severity="error" text="La note n'a pas pu être préparée sur cet appareil." />
			</ConditionalLoader>

			<div css={cardStyle}>
				<span css={cardLabelStyle}>NOTE DICTÉE</span>
				<div css={timeStyle}>00:00</div>
				<div css={barsStyle} aria-hidden="true">
					{IDLE_BARS.map((id) => (
						<span key={id} css={barStyle} />
					))}
				</div>
				<p css={hintStyle}>Dictez ce que vous avez observé ou ce que la personne a exprimé.</p>
				<ConditionalLoader data={!isWriteRefused}>
					<button
						type="button"
						css={startButtonStyle}
						disabled={isSubmitting}
						onClick={handleStartDictation}
					>
						<Mic size={18} aria-hidden="true" />
						Commencer la dictée
					</button>
				</ConditionalLoader>
			</div>

			<p css={noteStyle}>
				À la fin, la note est transcrite puis ajoutée aux éléments de la préparation. Rien n’est
				généré.
			</p>
		</div>
	);
};

export default NoteDictationContent;
